import { useCallback, useEffect, useMemo, useState } from 'react';

import { IPortfolioItemChildFragment } from 'graphql/types';
import { useSkillCertificateColumns } from 'skill/hooks/useSkillCertificateColumns';
import { useSkillCertificateList } from 'skill/hooks/useSkillCertificateList';

export interface ISkillCertificateColumn {
    id: string;
    name: string;
    isExternalCertificate: boolean;
    isMandatory: boolean;
}

export type TUserCertificates = Map<
    string,
    Map<string, IPortfolioItemChildFragment>
>;

interface IUseSkillReportColumnExpansionOptions {
    userIds: string[];
}

export const useSkillReportColumnExpansion = ({
    userIds,
}: IUseSkillReportColumnExpansionOptions) => {
    const [expandedSkillIds, setExpandedSkillIds] = useState<Set<string>>(
        new Set()
    );
    const [noCertsSkillIds, setNoCertsSkillIds] = useState<Set<string>>(
        new Set()
    );
    const [noCertsDialogSkillId, setNoCertsDialogSkillId] = useState<
        string | null
    >(null);
    const [certificatesBySkill, setCertificatesBySkill] = useState<
        Map<string, ISkillCertificateColumn[]>
    >(new Map());
    // Lookup: user → skill → certificate → portfolio item.
    const [userCertificates, setUserCertificates] = useState<
        Map<string, TUserCertificates>
    >(new Map());

    const expandedSkillIdsArray = useMemo(
        () => Array.from(expandedSkillIds),
        [expandedSkillIds]
    );
    // Only one skill can be expanded at a time.
    const expandedSkillId = expandedSkillIdsArray[0];

    const { skillCertificates, loading: loadingColumns } =
        useSkillCertificateColumns({
            skillId: expandedSkillId,
            skip: !expandedSkillId,
        });

    const {
        items: certificateItems,
        loading: loadingCertificates,
        refetch: refetchCertificates,
    } = useSkillCertificateList({
        queryVariables: {
            userIds,
            skillIds: expandedSkillIdsArray,
        },
        skip: !expandedSkillIdsArray.length || !userIds.length,
    });

    // When the visible users change (pagination/filter), clear per-user data
    // and collapse any expanded skill. Column data is kept — it's the same
    // regardless of which users are on screen.
    useEffect(() => {
        setExpandedSkillIds(new Set());
        setUserCertificates(new Map());
    }, [userIds]);

    // Columns come from the skill itself, not from which users are visible.
    // This keeps the column count stable across pages.
    useEffect(() => {
        if (!expandedSkillId) return;
        if (!skillCertificates.length) return;

        const columns: ISkillCertificateColumn[] = [];

        skillCertificates.forEach((entry) => {
            const certificate = entry?.certificate;

            if (!certificate) return;

            columns.push({
                id: certificate.id,
                name: certificate.name,
                isExternalCertificate: !!certificate.isExternalCertificate,
                isMandatory: !!entry.isMandatory,
            });
        });

        setCertificatesBySkill((prev) => {
            const next = new Map(prev);

            next.set(expandedSkillId, columns);

            return next;
        });
    }, [expandedSkillId, skillCertificates]);

    // Index cells by user → skill → certificate so body cells can look them
    // up directly.
    useEffect(() => {
        if (!certificateItems.length) return;

        const freshByUser = new Map<string, TUserCertificates>();

        certificateItems.forEach((item) => {
            const userId = item.user?.id;
            const skillId = item.relatedId;

            if (!userId || !skillId) return;

            if (!freshByUser.has(userId)) {
                freshByUser.set(userId, new Map());
            }

            const userMap = freshByUser.get(userId)!;

            if (!userMap.has(skillId)) {
                userMap.set(skillId, new Map());
            }

            const skillMap = userMap.get(skillId)!;

            item.children?.forEach((child) => {
                const certId = child.metadata?.certificateId;

                if (certId) {
                    skillMap.set(certId, child);
                }
            });
        });

        setUserCertificates((prev) => {
            const next = new Map(prev);

            freshByUser.forEach((freshUserMap, userId) => {
                const existing = next.get(userId) || new Map();
                const merged = new Map(existing);

                freshUserMap.forEach((skillMap, skillId) =>
                    merged.set(skillId, skillMap)
                );
                next.set(userId, merged);
            });

            return next;
        });
    }, [certificateItems]);

    // If a skill has no certificates configured, mark it and show a dialog
    // instead of expanding it.
    useEffect(() => {
        if (loadingColumns) return;
        if (!expandedSkillId) return;
        if (skillCertificates.length > 0) return;

        setNoCertsSkillIds((prev) => {
            if (prev.has(expandedSkillId)) return prev;

            return new Set(prev).add(expandedSkillId);
        });
        setExpandedSkillIds(new Set());
        setNoCertsDialogSkillId(expandedSkillId);
    }, [loadingColumns, skillCertificates, expandedSkillId]);

    const toggleSkillExpansion = useCallback(
        (skillId: string) => {
            if (noCertsSkillIds.has(skillId)) {
                setNoCertsDialogSkillId(skillId);

                return;
            }

            setExpandedSkillIds((prev) => {
                if (prev.has(skillId)) {
                    return new Set();
                }

                return new Set([skillId]);
            });
        },
        [noCertsSkillIds]
    );

    const closeNoCertsDialog = useCallback(() => {
        setNoCertsDialogSkillId(null);
    }, []);

    return {
        expandedSkillIds,
        expandedSkillIdsArray,
        certificatesBySkill,
        userCertificates,
        loadingCertificates,
        refetchCertificates,
        noCertsDialogSkillId,
        toggleSkillExpansion,
        closeNoCertsDialog,
    };
};
